import { ReactElement, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { HoverContent, useNodeHover } from "@financial-advisory/ui/providers";
import { AnimatePresence, motion } from "framer-motion";
import { Spacing, SpacingProps } from "@unlikelyai-magic/ui/layouts";
import { useChatMessageState } from "../hooks";
import { Message, MessageAuthor } from "../types";
import { ChatMessage } from "./ChatMessage";
import { FadingScrollContainer } from "./FadingScrollContainer";
import { MessageInput } from "./MessageInput";

const MessagesContainer = styled(Spacing)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 2rem;
  padding-right: 1rem;
`;

type ChatContainerProps = SpacingProps & {
  hasInput?: boolean;
};

// Utility to get highlighted message indexes based on hovered content
function getHighlightedMessageIndexes(
  filteredMessages: Message[],
  hoveredNodeContent: HoverContent
) {
  if (!hoveredNodeContent.answer) return [];

  const messagesWithAnswer = filteredMessages.filter(
    (message) =>
      typeof message.text === "string" &&
      message?.text.toLowerCase() === hoveredNodeContent.answer.toLowerCase()
  );

  const messagesWithQuestion = filteredMessages.filter(
    (message) =>
      (typeof message.question === "string" &&
        hoveredNodeContent.question &&
        message?.question.toLowerCase() ===
          hoveredNodeContent.question.toLowerCase()) ||
      (typeof message.text === "string" &&
        hoveredNodeContent.chatQuestion &&
        message?.text
          ?.toLowerCase()
          .includes(hoveredNodeContent.chatQuestion.toLowerCase()))
  );

  // If there are answers but no matching questions, highlight the answer(s)
  if (messagesWithAnswer.length > 0 && messagesWithQuestion.length === 0) {
    return messagesWithAnswer.map((msg) => msg.id);
  }

  const hasSingleQuestion = messagesWithQuestion.length === 1;
  const hasSingleAnswer = messagesWithAnswer.length === 1;
  // If there is a single question and a single answer, highlight both
  if (hasSingleQuestion && hasSingleAnswer) {
    return [messagesWithQuestion[0].id, messagesWithAnswer[0].id];
  }

  // If there are matching questions, highlight both the question and its answer (if adjacent)
  const indexesToHighlight = new Set<string>();
  filteredMessages.forEach((message, index) => {
    const isQuestion = messagesWithQuestion.some((q) => q.id === message.id);
    if (isQuestion) {
      indexesToHighlight.add(message.id);
      const nextMessage = filteredMessages[index + 1];
      const isNextAnswer =
        nextMessage && messagesWithAnswer.some((a) => a.id === nextMessage.id);
      if (isNextAnswer) {
        indexesToHighlight.add(nextMessage.id);
      }
    }
  });
  return Array.from(indexesToHighlight);
}

export const ChatContainer = ({
  hasInput = true,
  ...props
}: ChatContainerProps) => {
  const { messages } = useChatMessageState();
  const { hoveredNodeContent } = useNodeHover();

  const [highlightedMessageIndexes, setHighlightedMessageIndexes] = useState<
    string[]
  >([]);

  useEffect(() => {
    const filteredMessages = messages.filter(
      (message) => message.author !== MessageAuthor.Auditor
    );
    const newIndexes = getHighlightedMessageIndexes(
      filteredMessages,
      hoveredNodeContent
    );
    setHighlightedMessageIndexes((prev) =>
      prev.join() !== newIndexes.join() ? newIndexes : prev
    );
  }, [messages, hoveredNodeContent]);

  const animateMessage = (message: Message, index: number): ReactElement => {
    return (
      <motion.div
        key={message.id}
        initial={{
          opacity: 0,
          translateY: index % 2 === 0 ? -24 : 24,
        }}
        animate={{ opacity: 1, translateY: 0 }}
      >
        <ChatMessage
          message={message}
          isHighlighted={highlightedMessageIndexes.includes(message.id)}
        />
      </motion.div>
    );
  };

  return (
    <Spacing {...props} gap="none">
      <FadingScrollContainer style={{ flex: 1, minHeight: 0 }}>
        <MessagesContainer direction="vertical" gap="lg">
          <AnimatePresence mode="popLayout">
            {messages
              .filter((message) => message.author !== MessageAuthor.Auditor)
              .map((message, index) => animateMessage(message, index))}
          </AnimatePresence>
        </MessagesContainer>
      </FadingScrollContainer>
      {hasInput && <MessageInput />}
    </Spacing>
  );
};
